import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, ShieldCheck, Zap, Wallet,
  Network, Lock, Loader2, ChevronDown, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RoiCalculator from "@/components/marketing/RoiCalculator";
import PilotOfferStrip from "@/components/marketing/PilotOfferStrip";
import SectorCTA from "@/components/marketing/SectorCTA";

/**
 * /buy-leads — premium calculator-first commerce page (iter 55).
 *
 * Aesthetic: dark editorial hero, abstract illustrations (inline SVG, Stripe/Linear style),
 * full-bleed calculator IS the page. No "free" language. Premium-positioned.
 *
 * Conversion path:
 *   1. Hero introduces the product (vetted exclusive UK finance leads)
 *   2. Calculator (no scroll required on desktop)
 *   3. Trust band (sectors covered, compliance, infra signals)
 *   4. Order modal → Stripe Checkout OR Bank-transfer invoice
 *   5. /buy-leads/order-success on payment
 */
export default function BuyLeads() {
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [sectorSlug, setSectorSlug] = useState("mortgages");
  const [qty, setQty] = useState(100);
  const [delivery, setDelivery] = useState("webhook");

  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  const [orderOpen, setOrderOpen] = useState(false);

  // iter96: retry-with-backoff so a transient cold-start hiccup on the
  // pricing fetch doesn't silently render an empty calculator.
  const loadMeta = async () => {
    setMetaLoading(true);
    setMetaError(false);
    const delays = [0, 400, 900, 1800];
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt]) await new Promise((r) => setTimeout(r, delays[attempt]));
      try {
        const { data } = await api.get("/lead-packs/meta");
        if (data && Array.isArray(data.sectors) && data.sectors.length > 0) {
          setMeta(data);
          setMetaLoading(false);
          return;
        }
      } catch (_) {
        // fall through to next retry
      }
    }
    setMetaError(true);
    setMetaLoading(false);
    toast.error("Could not load sector pricing — tap Try again");
  };

  useEffect(() => { loadMeta(); }, []);

  // Live re-quote whenever inputs change (server is the source of truth)
  useEffect(() => {
    if (!meta) return;
    let cancelled = false;
    (async () => {
      setQuoting(true);
      try {
        const { data } = await api.post("/lead-packs/quote", {
          sector: sectorSlug, quantity: qty, delivery,
        });
        if (!cancelled) setQuote(data);
      } catch (_) {
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setQuoting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [meta, sectorSlug, qty, delivery]);

  return (
    <div className="bg-slate-50">
      {/* ──────────── HERO ──────────── */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <BackgroundGrid />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:pt-32 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-widest text-white/70"
                data-testid="buy-leads-eyebrow"
              >
                <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Buy exclusive UK finance leads
              </div>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[1.04]">
                Premium leads. <span className="text-[#60A5FA]">Exclusive only.</span> <br className="hidden lg:block" />Delivered live to your CRM.
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] leading-relaxed text-white/70 max-w-2xl">
                Connexis sells <b className="text-white">100% exclusive</b> finance leads — every enquiry is delivered to a single buyer, never resold, never duplicated. Choose your sector, set your volume, pay once, receive leads within 24 hours.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href="#calculator">
                  <Button className="rounded-full h-12 px-6 bg-white text-slate-900 hover:bg-slate-100 text-[14px] font-semibold" data-testid="buy-leads-hero-cta">
                    Build your order
                    <ArrowRight className="size-4" />
                  </Button>
                </a>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full h-12 px-5 text-white/80 hover:text-white hover:bg-white/10 text-[14px]" data-testid="buy-leads-cta-signin">
                    Already a buyer? Sign in <ArrowUpRight className="size-4" />
                  </Button>
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-2xl">
                <HeroStat label="Leads routed / month" value="240k+" />
                <HeroStat label="Median delivery time" value="98ms" />
                <HeroStat label="Exclusive rate" value="100%" />
                <HeroStat label="FCA-aware compliance" value="Built-in" />
              </div>
            </div>

            {/* Right-side abstract illustration */}
            <div className="lg:col-span-5 hidden lg:block">
              <HeroIllustration />
            </div>
          </div>
        </div>
      </section>

      {/* ──────────── CALCULATOR ──────────── */}
      <section id="calculator" className="relative -mt-16 lg:-mt-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] ring-1 ring-slate-900/5 overflow-hidden">

            {/* Calc header */}
            <div className="px-6 sm:px-10 pt-8 pb-6 border-b border-slate-100">
              <div className="text-[11px] uppercase tracking-widest text-[#0055FF] font-semibold">Build your order</div>
              <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Configure your campaign — see your exact price.</h2>
              <p className="mt-2 text-[14px] text-slate-600 max-w-2xl">Pricing is fixed, delivered as you select. No quote calls, no negotiations.</p>
            </div>

            {/* Calc grid */}
            {metaError ? (
              <CalcErrorState onRetry={loadMeta} />
            ) : metaLoading || !meta ? (
              <CalcSkeleton />
            ) : (
            <div className="grid lg:grid-cols-12 gap-0">
              {/* Left: inputs */}
              <div className="lg:col-span-7 p-6 sm:p-10 space-y-9">
                {/* ─ Sector ─ */}
                <Step n={1} title="Choose your sector">
                  <div className="grid sm:grid-cols-2 gap-2.5" data-testid="calc-sectors">
                    {(meta?.sectors || []).map((s) => (
                      <button
                        key={s.slug}
                        onClick={() => setSectorSlug(s.slug)}
                        className={`text-left rounded-xl border px-4 py-3.5 transition-all ${
                          sectorSlug === s.slug
                            ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/15"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`calc-sector-${s.slug}`}
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="text-[13.5px] font-semibold text-slate-900 truncate">{s.name}</div>
                          <div className="text-[13px] font-semibold text-slate-900 tabular-nums shrink-0">£{s.base_cpl}<span className="text-[10px] text-slate-500 ml-0.5">/lead</span></div>
                        </div>
                      </button>
                    ))}
                  </div>
                </Step>

                {/* ─ Volume ─ */}
                <Step n={2} title="Set your volume">
                  <VolumeStepper qty={qty} setQty={setQty} tiers={meta?.volume_tiers || []} />
                </Step>

                {/* ─ Delivery ─ */}
                <Step n={3} title="Choose delivery">
                  <div className="grid sm:grid-cols-3 gap-2.5">
                    {(meta?.delivery || []).map((d) => (
                      <button
                        key={d.slug}
                        onClick={() => setDelivery(d.slug)}
                        className={`text-left rounded-xl border p-4 transition-all ${
                          delivery === d.slug
                            ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/15"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`calc-delivery-${d.slug}`}
                      >
                        <div className="text-[13.5px] font-semibold text-slate-900">{d.label}</div>
                        <div className="mt-1 text-[12px] text-slate-500 leading-snug">{d.desc}</div>
                        <div className="mt-2 text-[11px] tabular-nums font-medium text-slate-700">
                          {d.setup_gbp > 0 ? `£${d.setup_gbp} one-time setup` : "No setup fee"}
                        </div>
                      </button>
                    ))}
                  </div>
                </Step>

                {/* Exclusivity locked-in card */}
                <div className="rounded-xl border border-emerald-200/60 bg-emerald-50/40 p-4 flex items-start gap-3" data-testid="calc-exclusivity">
                  <ShieldCheck className="size-5 text-emerald-600 shrink-0 mt-0.5" strokeWidth={1.6} />
                  <div>
                    <div className="text-[13px] font-semibold text-emerald-900">100% Exclusive — included on every order</div>
                    <div className="mt-0.5 text-[12.5px] text-emerald-800/80">Every lead is sold to one buyer only. Never resold, never duplicated.</div>
                  </div>
                </div>
              </div>

              {/* Right: live summary */}
              <div className="lg:col-span-5 lg:border-l border-t lg:border-t-0 border-slate-100 bg-slate-50/60 p-6 sm:p-10">
                <SummaryPanel
                  quote={quote}
                  quoting={quoting}
                  onOrder={() => setOrderOpen(true)}
                />
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* ──────────── TRUST BAND ──────────── */}
      <section className="border-y border-slate-200/70 bg-white mt-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid sm:grid-cols-3 gap-8 items-start">
            <TrustCard Icon={Lock} title="100% exclusive · always" body="Every Connexis lead is sold to exactly one buyer. Never resold, never duplicated, never shopped around." />
            <TrustCard Icon={ShieldCheck} title="FCA-aware compliance" body="TrustedForm, consent timestamps, IP/UA capture. Every lead audit-ready for the FCA, FOS or ICO." />
            <TrustCard Icon={Zap} title="Sub-second delivery" body="Median 98ms from form-submit to your CRM. Engineered to outperform every direct-post broker in the UK." />
          </div>
        </div>
      </section>

      {/* ──────────── HOW IT WORKS ──────────── */}
      <section className="bg-white border-b border-slate-200/70">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-widest text-[#0055FF] font-semibold">How it works</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">From order to live leads in 24 hours.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <StepCard n="01" title="Build your order"      body="Pick sector, volume and delivery. See the full price before you commit." />
            <StepCard n="02" title="Pay securely"          body="Stripe card payment or bank-transfer invoice. PDF receipt emailed immediately." />
            <StepCard n="03" title="Connect your CRM"      body="Paste your webhook URL or pick your CRM — our team verifies delivery end-to-end." />
            <StepCard n="04" title="Receive live leads"    body="Sub-second routing. Exclusive to you. Track every lead in your dashboard." />
          </div>
        </div>
      </section>

      {/* ──────────── ROI CALCULATOR ──────────── */}
      <RoiCalculator ctaHref="#calculator" />

      {/* ──────────── SECTOR NOT LISTED ──────────── */}
      <SectorCTA />

      {/* ──────────── FUNDED PILOT ──────────── */}
      <PilotOfferStrip />

      {/* ──────────── FINAL CTA ──────────── */}
      <section className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">Ready to take only the best leads?</h2>
              <p className="mt-4 text-white/70 text-[15px] max-w-xl">No subscriptions. No setup fees on standard delivery. Pay for the exact volume you ordered — and own every lead exclusively.</p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end gap-3">
              <a href="#calculator">
                <Button className="rounded-full h-12 px-6 bg-white text-slate-900 hover:bg-slate-100 font-semibold" data-testid="buy-leads-final-cta">
                  Build your order <ArrowRight className="size-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────── ORDER MODAL ──────────── */}
      <OrderModal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        sector={sectorSlug}
        quantity={qty}
        delivery={delivery}
        quote={quote}
      />
    </div>
  );
}

/* ════════════════ small atoms ════════════════ */

function Step({ n, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center size-5 rounded-md bg-slate-900 text-white text-[10px] font-mono font-semibold">{n}</span>
        <span className="text-[13px] font-semibold text-slate-900 uppercase tracking-wide">{title}</span>
      </div>
      {children}
    </div>
  );
}

function HeroStat({ label, value }) {
  return (
    <div>
      <div className="text-2xl sm:text-3xl font-semibold tabular-nums text-white">{value}</div>
      <div className="text-[10.5px] uppercase tracking-widest text-white/50 mt-1">{label}</div>
    </div>
  );
}

function TrustCard({ Icon, title, body }) {
  return (
    <div>
      <Icon className="size-6 text-[#0055FF] mb-4" strokeWidth={1.5} />
      <div className="text-[16px] font-semibold text-slate-900">{title}</div>
      <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

function StepCard({ n, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 transition-colors">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-4">{n}</div>
      <div className="text-[15px] font-semibold text-slate-900">{title}</div>
      <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

/* ════════════════ Volume stepper ════════════════ */
function VolumeStepper({ qty, setQty, tiers }) {
  const PRESETS = [25, 50, 100, 250, 500];
  const handleSlider = (e) => setQty(parseInt(e.target.value, 10));
  const handleInput = (e) => {
    const v = parseInt(e.target.value, 10);
    if (!isNaN(v)) setQty(Math.max(25, Math.min(999, v)));
  };
  const currentTier = (tiers || []).find((t) => qty >= t.min && qty <= t.max);
  const discount = currentTier ? Math.round(currentTier.discount * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={qty}
          onChange={handleInput}
          min={25}
          max={999}
          className="w-28 rounded-lg border border-slate-200 px-3 py-2.5 text-[14px] font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-[#0055FF]/20 focus:border-[#0055FF]"
          data-testid="calc-qty-input"
        />
        <span className="text-[13px] text-slate-600">leads · between <span className="font-medium">25</span> and <span className="font-medium">999</span></span>
        {discount > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 text-[11px] font-semibold px-2 py-0.5">−{discount}% volume</span>
        )}
      </div>
      <input
        type="range"
        min={25}
        max={999}
        step={25}
        value={qty}
        onChange={handleSlider}
        className="w-full accent-[#0055FF]"
        data-testid="calc-qty-slider"
      />
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p}
            onClick={() => setQty(p)}
            className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors ${
              qty === p
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
            data-testid={`calc-qty-preset-${p}`}
          >
            {p} leads
          </button>
        ))}
      </div>
      <div className="text-[11px] text-slate-500">
        Volume tiers: 100+ saves 5% · 250+ saves 10% · 500+ saves 15% · <Link to="/contact" className="text-[#0055FF] hover:underline">need 1,000+? talk to us</Link>
      </div>
    </div>
  );
}

/* ════════════════ Summary panel ════════════════ */
function SummaryPanel({ quote, quoting, onOrder }) {
  const p = quote?.pricing;
  return (
    <div className="sticky top-24">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Your order</div>

      {quoting && (
        <div className="mt-2 text-[11px] text-slate-400 inline-flex items-center gap-1">
          <Loader2 className="size-3 animate-spin" /> recalculating…
        </div>
      )}

      <div className="mt-6 space-y-3">
        <Row label="Sector"        value={quote?.sector?.name || "—"} />
        <Row label="Quantity"      value={quote ? `${quote.quantity.toLocaleString()} leads` : "—"} />
        <Row label="Per-lead price" value={p ? `£${p.cpl_final.toFixed(2)}` : "—"} mono />
        {p?.volume_discount > 0 && (
          <Row label={`Volume discount (${p.discount_pct_label})`} value={`−£${p.savings_gbp.toLocaleString()}`} positive mono />
        )}
        <Row label="Delivery"       value={quote?.delivery?.label || "—"} />
        {p?.delivery_setup > 0 && (
          <Row label="Setup fee"    value={`£${p.delivery_setup.toLocaleString()}`} mono />
        )}
      </div>

      <div className="my-6 h-px bg-slate-200" />

      <div className="flex items-baseline justify-between">
        <span className="text-[13px] uppercase tracking-widest text-slate-500 font-semibold">Total</span>
        <span className="text-3xl font-semibold tracking-tight text-slate-900 tabular-nums" data-testid="calc-total">
          {p ? `£${p.total_gbp.toLocaleString()}` : "—"}
        </span>
      </div>

      <Button
        onClick={onOrder}
        disabled={!quote || quoting}
        className="mt-6 w-full h-12 rounded-full text-[14px] font-semibold"
        data-testid="calc-order-btn"
      >
        Order leads · {p ? `£${p.total_gbp.toLocaleString()}` : "—"}
        <ArrowRight className="size-4" />
      </Button>

      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="size-3 text-emerald-500" /> Card or bank transfer</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="size-3 text-emerald-500" /> PDF invoice emailed</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="size-3 text-emerald-500" /> Live in &lt;24h</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="size-3 text-emerald-500" /> Exclusive — never resold</span>
      </div>
    </div>
  );
}

function Row({ label, value, mono, positive }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[13px] text-slate-600">{label}</span>
      <span className={`text-[13.5px] tabular-nums ${positive ? "text-emerald-600 font-medium" : "text-slate-900"} ${mono ? "" : ""}`}>{value}</span>
    </div>
  );
}

/* ════════════════ Order modal ════════════════ */
function OrderModal({ open, onClose, sector, quantity, delivery, quote }) {
  const { user } = useAuth();
  const [pm, setPm] = useState("card");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", notes: "" });
  // iter86: when a buyer is already logged in, pre-fill from AuthContext and
  // hide the form — they shouldn't have to retype their identity to re-order.
  const [editing, setEditing] = useState(false);

  // Seed form from logged-in user whenever the modal opens
  useEffect(() => {
    if (!open) return;
    if (user) {
      setForm((s) => ({
        ...s,
        company: user.company || s.company,
        name:    user.name    || s.name,
        email:   user.email   || s.email,
        phone:   user.phone   || s.phone,
      }));
      setEditing(false);
    } else {
      setEditing(true);
    }
  }, [open, user]);

  const handle = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!form.company || !form.name || !form.email) {
      toast.error("Company, name and email are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        sector, quantity, delivery,
        payment_method: pm,
        origin_url: window.location.origin,
        ...form,
      };
      const { data } = await api.post("/lead-packs/checkout", payload);
      if (data.payment_method === "bank_transfer") {
        toast.success(`Invoice ${data.invoice_number} emailed to ${form.email}`);
        window.location.href = `/buy-leads/order-success?order_id=${data.order_id}&method=bank_transfer${user ? "&signed_in=1" : ""}`;
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start order");
    } finally {
      setBusy(false);
    }
  };

  const p = quote?.pricing;
  const identityComplete = user && form.company && form.name && form.email && !editing;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="order-modal">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight">
            Complete your order — {p ? `£${p.total_gbp.toLocaleString()}` : "—"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-[12.5px] text-slate-700">
            <b>{quantity}</b> × <b>{quote?.sector?.name}</b> leads · 100% exclusive · {quote?.delivery?.label}
          </div>

          {/* iter86: signed-in identity card (read-only) OR editable form */}
          {identityComplete ? (
            <div
              className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-start justify-between gap-3"
              data-testid="order-identity-summary"
            >
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Billed to</div>
                <div className="mt-0.5 text-[13.5px] font-medium text-slate-900 truncate">{form.company}</div>
                <div className="text-[12px] text-slate-600 truncate">{form.name} · {form.email}</div>
                {form.phone && <div className="text-[11.5px] text-slate-500 truncate">{form.phone}</div>}
              </div>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="shrink-0 inline-flex items-center gap-1 text-[12px] font-medium text-[#0055FF] hover:underline"
                data-testid="order-identity-edit"
              >
                <Pencil className="size-3" /> Edit
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] text-slate-700">Company *</Label>
                <Input value={form.company} onChange={handle("company")} data-testid="order-company" />
              </div>
              <div>
                <Label className="text-[12px] text-slate-700">Your name *</Label>
                <Input value={form.name} onChange={handle("name")} data-testid="order-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-[12px] text-slate-700">Work email *</Label>
                <Input type="email" value={form.email} onChange={handle("email")} data-testid="order-email" />
              </div>
              <div className="col-span-2">
                <Label className="text-[12px] text-slate-700">Phone (optional)</Label>
                <Input value={form.phone} onChange={handle("phone")} data-testid="order-phone" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-[12px] text-slate-700">Payment method</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {["card", "bank_transfer"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPm(m)}
                  className={`rounded-lg border px-3 py-2.5 text-left transition-all ${
                    pm === m
                      ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/15"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`order-pm-${m}`}
                >
                  <div className="text-[13px] font-semibold text-slate-900">
                    {m === "card" ? "Card · Stripe" : "Bank transfer"}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {m === "card" ? "Live leads on payment" : "Live leads on funds cleared"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <Button
            disabled={busy}
            onClick={submit}
            className="w-full h-12 rounded-full font-semibold"
            data-testid="order-submit-btn"
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <>Continue · {p ? `£${p.total_gbp.toLocaleString()}` : ""}<ArrowRight className="size-4" /></>}
          </Button>

          <p className="text-[11px] text-slate-500 text-center">
            By ordering you agree to our <Link to="/docs" className="underline">Terms</Link>. PDF invoice will be emailed to {form.email || "your email"}.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ════════════════ Hero visuals ════════════════ */

function BackgroundGrid() {
  return (
    <>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(1200px 480px at 20% 0%, rgba(0,85,255,0.22), transparent 65%), radial-gradient(900px 400px at 90% 30%, rgba(96,165,250,0.18), transparent 60%)",
        }}
      />
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.08]" aria-hidden>
        <defs>
          <pattern id="hg" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#fff" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hg)" />
      </svg>
    </>
  );
}

function HeroIllustration() {
  // Stripe/Linear style abstract: a stack of "lead order" cards floating with subtle connections to a CRM endpoint.
  return (
    <div className="relative w-full aspect-square max-w-md ml-auto">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] ring-1 ring-white/10 backdrop-blur" />
      <div className="relative h-full flex flex-col justify-center px-6 py-8 gap-3">
        <FloatCard delay={0} sector="Mortgages" qty={100} price="£3,610" />
        <FloatCard delay={0.5} sector="Business Finance" qty={250} price="£11,700" highlight />
        <FloatCard delay={1.0} sector="Life Insurance" qty={50} price="£1,700" />
        <FloatCard delay={1.5} sector="Wealth Management" qty={75} price="£7,125" />

        {/* Arrow → CRM */}
        <div className="absolute -right-2 top-1/2 -translate-y-1/2 hidden">
          <div className="rounded-xl bg-white/10 backdrop-blur ring-1 ring-white/10 px-3 py-2 text-[11px] text-white/80">your-crm</div>
        </div>
      </div>
    </div>
  );
}

function FloatCard({ sector, qty, price, highlight, delay }) {
  return (
    <div
      className={`rounded-xl ring-1 ${highlight ? "bg-[#0055FF]/15 ring-[#60A5FA]/40" : "bg-white/[0.05] ring-white/10"} backdrop-blur px-4 py-3 flex items-center justify-between gap-4`}
      style={{ animation: `floatY 4s ease-in-out ${delay}s infinite alternate` }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`size-2 rounded-full ${highlight ? "bg-[#60A5FA]" : "bg-emerald-400"}`} />
        <div className="text-[13px] font-medium text-white/90 truncate">{sector}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-[11px] text-white/50 tabular-nums">{qty} leads</div>
        <div className="text-[13px] font-semibold text-white tabular-nums">{price}</div>
      </div>
      <style>{`@keyframes floatY {0%{transform:translateY(-2px)}100%{transform:translateY(2px)}}`}</style>
    </div>
  );
}

/* ─────────────────────────  P1 — meta loading + error states  ───────────────────────── */

function CalcSkeleton() {
  return (
    <div
      className="grid lg:grid-cols-12 gap-0 animate-pulse"
      data-testid="buy-leads-meta-skeleton"
      aria-busy="true"
      aria-label="Loading sector pricing"
    >
      <div className="lg:col-span-7 p-6 sm:p-10 space-y-9">
        {[1, 2, 3].map((s) => (
          <div key={s}>
            <div className="flex items-center gap-3 mb-4">
              <div className="size-7 rounded-full bg-slate-200" />
              <div className="h-4 w-44 bg-slate-200 rounded" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-[58px] rounded-xl bg-slate-100 border border-slate-200/70" />
              ))}
            </div>
          </div>
        ))}
        <div className="h-[68px] rounded-xl bg-emerald-50/40 border border-emerald-200/40" />
      </div>
      <div className="lg:col-span-5 lg:border-l border-t lg:border-t-0 border-slate-100 bg-slate-50/60 p-6 sm:p-10 space-y-4">
        <div className="h-4 w-24 bg-slate-200 rounded" />
        <div className="h-9 w-40 bg-slate-200 rounded" />
        <div className="space-y-2 pt-4">
          <div className="h-3 w-full bg-slate-100 rounded" />
          <div className="h-3 w-5/6 bg-slate-100 rounded" />
          <div className="h-3 w-2/3 bg-slate-100 rounded" />
        </div>
        <div className="h-12 w-full bg-slate-200 rounded-full mt-6" />
      </div>
    </div>
  );
}

function CalcErrorState({ onRetry }) {
  return (
    <div
      className="p-10 sm:p-16 text-center"
      data-testid="buy-leads-meta-error"
      role="alert"
    >
      <div className="mx-auto size-12 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center mb-5">
        <svg className="size-6 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
          <circle cx="12" cy="12" r="9.5" />
        </svg>
      </div>
      <h3 className="text-[17px] font-semibold text-slate-900">We couldn’t load sector pricing</h3>
      <p className="mt-2 text-[13.5px] text-slate-600 max-w-md mx-auto">
        Sector & volume pricing comes live from our routing engine. The request didn’t respond after 3 retries — usually a transient network hiccup.
      </p>
      <button
        onClick={onRetry}
        className="mt-6 inline-flex items-center gap-2 rounded-full h-11 px-6 bg-slate-900 hover:bg-slate-800 text-white text-[13.5px] font-semibold transition-colors"
        data-testid="buy-leads-meta-retry"
      >
        Try again
        <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M3 12a9 9 0 1 0 3-6.7" strokeLinecap="round" />
          <path d="M3 4v5h5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div className="mt-5 text-[12px] text-slate-500">
        Still failing?{" "}
        <a href="mailto:ops@connex.is" className="text-[#0055FF] hover:underline">Email ops@connex.is</a>
      </div>
    </div>
  );
}

